<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="400"
    @click:outside="false"
  >
    <v-card class="incoming-call-card">
      <!-- ========== RINGING STATE ========== -->
      <template v-if="!isConnected">
        <v-card-title class="ringing-header white--text d-flex flex-column align-center py-6">
          <v-avatar size="80" color="white" class="mb-3 pulse-ring">
            <v-icon size="60" color="green">mdi-phone-incoming</v-icon>
          </v-avatar>
          <h3 class="mb-2">Incoming Call</h3>
        </v-card-title>

        <v-card-text class="text-center pt-6 pb-4">
          <h2 class="mb-2 primary--text">
            {{ callerName || formatPhoneNumber(callDetails.from) }}
          </h2>
          <p class="text-subtitle-1 grey--text">
            {{ callDetails.from }}
          </p>
          <v-chip
            v-if="callDetails.did"
            small
            outlined
            color="primary"
            class="mt-2"
          >
            <v-icon small left>mdi-phone-forward</v-icon>
            DID: {{ callDetails.did }}
          </v-chip>
          
          <div v-if="callDetails.strategy" class="mt-3">
            <v-chip
              x-small
              :color="getStrategyColor(callDetails.strategy)"
              text-color="white"
            >
              <v-icon x-small left>{{ getStrategyIcon(callDetails.strategy) }}</v-icon>
              {{ getStrategyLabel(callDetails.strategy) }}
            </v-chip>
          </div>
        </v-card-text>

        <v-card-text v-if="status && status !== 'ringing'" class="text-center py-2">
          <v-chip :color="getStatusColor(status)" text-color="white" small>
            <v-icon small left>{{ getStatusIcon(status) }}</v-icon>
            {{ getStatusLabel(status) }}
          </v-chip>
        </v-card-text>

        <v-card-actions class="pa-4 d-flex justify-space-around">
          <v-btn
            x-large
            fab
            color="red"
            dark
            :disabled="isProcessing"
            @click="rejectCall"
          >
            <v-icon size="32">mdi-phone-hangup</v-icon>
          </v-btn>

          <v-btn
            x-large
            fab
            color="green"
            dark
            :disabled="isProcessing"
            @click="acceptCall"
          >
            <v-icon size="32">mdi-phone</v-icon>
          </v-btn>
        </v-card-actions>

        <v-card-text class="text-center pb-4 pt-0">
          <p class="caption grey--text mb-0">
            Decline or answer the call
          </p>
        </v-card-text>
      </template>

      <!-- ========== CONNECTED STATE ========== -->
      <template v-else>
        <v-card-title class="connected-header white--text d-flex flex-column align-center py-4">
          <v-avatar size="60" color="white" class="mb-2">
            <v-icon size="40" color="green">mdi-phone-in-talk</v-icon>
          </v-avatar>
          <h4 class="mb-1">Call Connected</h4>
          <div class="call-duration-display">
            {{ formatDuration(callDuration) }}
          </div>
        </v-card-title>

        <v-card-text class="text-center pt-4 pb-2">
          <h3 class="mb-1 primary--text">
            {{ callerName || formatPhoneNumber(callDetails.from) }}
          </h3>
          <p class="text-subtitle-2 grey--text mb-0">
            {{ callDetails.from }}
          </p>
          <v-chip
            v-if="callDetails.did"
            x-small
            outlined
            color="primary"
            class="mt-2"
          >
            <v-icon x-small left>mdi-phone-forward</v-icon>
            DID: {{ callDetails.did }}
          </v-chip>
        </v-card-text>

        <!-- Call Controls -->
        <v-card-text class="py-2">
          <div class="call-controls d-flex justify-center align-center">
            <!-- Mute Button -->
            <v-btn
              fab
              small
              :color="isMuted ? 'red' : 'grey lighten-2'"
              :dark="isMuted"
              class="mx-2"
              @click="toggleMute"
            >
              <v-icon>{{ isMuted ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
            </v-btn>

            <!-- Hold Button -->
            <v-btn
              fab
              small
              :color="isOnHold ? 'orange' : 'grey lighten-2'"
              :dark="isOnHold"
              class="mx-2"
              @click="toggleHold"
            >
              <v-icon>{{ isOnHold ? 'mdi-play' : 'mdi-pause' }}</v-icon>
            </v-btn>

            <!-- End Call Button -->
            <v-btn
              fab
              large
              color="red"
              dark
              class="mx-2"
              :disabled="isProcessing"
              @click="endCall"
            >
              <v-icon size="28">mdi-phone-hangup</v-icon>
            </v-btn>

            <!-- Dialpad Button -->
            <v-btn
              fab
              small
              :color="showDialpad ? 'primary' : 'grey lighten-2'"
              :dark="showDialpad"
              class="mx-2"
              @click="toggleDialpad"
            >
              <v-icon>mdi-dialpad</v-icon>
            </v-btn>

            <!-- Speaker Button -->
            <v-btn
              fab
              small
              :color="isSpeakerOn ? 'blue' : 'grey lighten-2'"
              :dark="isSpeakerOn"
              class="mx-2"
              @click="toggleSpeaker"
            >
              <v-icon>{{ isSpeakerOn ? 'mdi-volume-high' : 'mdi-volume-medium' }}</v-icon>
            </v-btn>
          </div>
        </v-card-text>

        <!-- DTMF Dialpad -->
        <v-expand-transition>
          <v-card-text v-show="showDialpad" class="pt-0">
            <div class="dtmf-dialpad">
              <v-row no-gutters class="dialpad-grid">
                <v-col v-for="digit in dialpadDigits" :key="digit.num" cols="4" class="pa-1">
                  <v-btn
                    class="dialpad-btn"
                    outlined
                    block
                    @click="sendDTMF(digit.num)"
                  >
                    <div class="text-center">
                      <div class="digit-main">{{ digit.num }}</div>
                      <div class="digit-sub">{{ digit.letters }}</div>
                    </div>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-expand-transition>

        <!-- Status Bar -->
        <v-card-text class="text-center py-2">
          <div class="d-flex justify-center align-center">
            <v-icon small :color="getStatusColor(status)" class="mr-1">
              {{ getStatusIcon(status) }}
            </v-icon>
            <span class="caption" :class="`${getStatusColor(status)}--text`">
              {{ getStatusLabel(status) }}
            </span>
            <span v-if="isOnHold" class="caption orange--text ml-2">
              • On Hold
            </span>
            <span v-if="isMuted" class="caption red--text ml-2">
              • Muted
            </span>
          </div>
        </v-card-text>
      </template>
    </v-card>

    <!-- Hidden Audio Element with mobile-specific attributes -->
    <audio 
      id="incoming-call-audio" 
      ref="incomingAudio" 
      autoplay 
      playsinline 
      webkit-playsinline
      x-webkit-airplay="allow"
      style="display: none;"
    ></audio>
  </v-dialog>
</template>

<script>
export default {
  name: "IncomingCallDialog",
  
  data() {
    return {
      isProcessing: false,
      isMuted: false,
      isOnHold: false,
      isSpeakerOn: false,
      showDialpad: false,
      dialpadDigits: [
        { num: '1', letters: '' },
        { num: '2', letters: 'ABC' },
        { num: '3', letters: 'DEF' },
        { num: '4', letters: 'GHI' },
        { num: '5', letters: 'JKL' },
        { num: '6', letters: 'MNO' },
        { num: '7', letters: 'PQRS' },
        { num: '8', letters: 'TUV' },
        { num: '9', letters: 'WXYZ' },
        { num: '*', letters: '' },
        { num: '0', letters: '+' },
        { num: '#', letters: '' },
      ],
    };
  },

  computed: {
    visible() {
      return this.$store.getters["dialer/hasIncomingCall"];
    },
    
    callDetails() {
      return this.$store.getters["dialer/incomingCallDetails"] || {};
    },
    
    callerName() {
      return this.callDetails.callerName || this.callDetails.name || "";
    },
    
    status() {
      return (this.$store.state.dialer && this.$store.state.dialer.incomingStatus) || "ringing";
    },

    isConnected() {
      return this.status === 'connected' || this.status === 'connecting';
    },

    callDuration() {
      const dialer = this.$store.state.dialer;
      return (dialer && dialer.callDuration) || 0;
    },
  },

  watch: {
    // Reset state when dialog closes
    visible(newVal) {
      if (!newVal) {
        this.isMuted = false;
        this.isOnHold = false;
        this.isSpeakerOn = false;
        this.showDialpad = false;
      }
    }
  },

  methods: {
    formatPhoneNumber(phone) {
      if (!phone) return "Unknown";
      const cleaned = String(phone).replace(/\D/g, "");
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      if (cleaned.length === 11 && cleaned[0] === "1") {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
      return phone;
    },

    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    getStrategyColor(strategy) {
      const colors = {
        agent: "blue",
        "random-agent": "orange",
        unassigned: "grey",
      };
      return colors[strategy] || "grey";
    },

    getStrategyIcon(strategy) {
      const icons = {
        agent: "mdi-account",
        "random-agent": "mdi-shuffle-variant",
        unassigned: "mdi-cancel",
      };
      return icons[strategy] || "mdi-help-circle";
    },

    getStrategyLabel(strategy) {
      const labels = {
        agent: "Direct Agent",
        "random-agent": "Random Agent",
        unassigned: "Unassigned",
      };
      return labels[strategy] || strategy;
    },

    getStatusColor(status) {
      const colors = {
        ringing: "blue",
        connecting: "orange",
        connected: "green",
        failed: "red",
        rejected: "grey",
      };
      return colors[status] || "grey";
    },

    getStatusIcon(status) {
      const icons = {
        ringing: "mdi-phone-ring",
        connecting: "mdi-phone-sync",
        connected: "mdi-phone-in-talk",
        failed: "mdi-phone-cancel",
        rejected: "mdi-phone-remove",
      };
      return icons[status] || "mdi-phone";
    },

    getStatusLabel(status) {
      const labels = {
        ringing: "Ringing...",
        connecting: "Connecting...",
        connected: "Connected",
        failed: "Call Failed",
        rejected: "Call Rejected",
      };
      return labels[status] || status;
    },

    async acceptCall() {
      if (this.isProcessing) {
        console.log("[IncomingCallDialog] Accept already processing, ignoring");
        return;
      }
      console.log("[IncomingCallDialog] 📞 Accepting call");
      this.isProcessing = true;
      
      try {
        // Get the audio element
        const audioElement = this.$refs.incomingAudio || document.getElementById("incoming-call-audio");

        await this.$store.dispatch("dialer/answerIncomingCall", {
          audioElement,
        });
        
        console.log("[IncomingCallDialog] ✅ Call accepted successfully");
        // Keep processing true briefly to prevent accidental end-call clicks
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error("[IncomingCallDialog] ❌ Error accepting call:", error);
        this.$store.commit("dialer/SET_INCOMING_STATUS", "failed");
        
        // Show error notification
        if (this.$notify) {
          this.$notify({
            group: "main",
            type: "error",
            title: "Call Failed",
            text: error.message || "Failed to accept the call",
          });
        }
        
        // Close dialog after error
        setTimeout(() => {
          this.$store.commit("dialer/CLEAR_INCOMING_CALL");
        }, 2000);
      } finally {
        this.isProcessing = false;
      }
    },

    async rejectCall() {
      if (this.isProcessing) {
        console.log("[IncomingCallDialog] Reject already processing, ignoring");
        return;
      }
      console.log("[IncomingCallDialog] 📵 Rejecting call (pre-answer)");
      this.isProcessing = true;
      
      try {
        await this.$store.dispatch("dialer/rejectIncomingCall");
        console.log("[IncomingCallDialog] ✅ Call rejected");
      } catch (error) {
        console.error("[IncomingCallDialog] ⚠️  Error rejecting call:", error);
      } finally {
        this.isProcessing = false;
      }
    },

    async endCall() {
      if (this.isProcessing) {
        console.log("[IncomingCallDialog] End call already processing, ignoring");
        return;
      }
      console.log("[IncomingCallDialog] 📴 Ending connected call");
      this.isProcessing = true;
      
      try {
        await this.$store.dispatch("dialer/endCall");
        console.log("[IncomingCallDialog] ✅ Call ended");
      } catch (error) {
        console.error("[IncomingCallDialog] ⚠️  Error ending call:", error);
      } finally {
        this.isProcessing = false;
      }
    },

    toggleMute() {
      this.isMuted = !this.isMuted;
      console.log("[IncomingCallDialog] � Mute toggled:", this.isMuted);
      
      // Toggle audio track via the session stored in dialer
      this.$store.dispatch("dialer/toggleMute", this.isMuted);
    },

    toggleHold() {
      this.isOnHold = !this.isOnHold;
      console.log("[IncomingCallDialog] ⏸️ Hold toggled:", this.isOnHold);
      
      // Toggle hold via the session
      this.$store.dispatch("dialer/toggleHold", this.isOnHold);
    },

    toggleDialpad() {
      this.showDialpad = !this.showDialpad;
    },

    toggleSpeaker() {
      this.isSpeakerOn = !this.isSpeakerOn;
      console.log("[IncomingCallDialog] � Speaker toggled:", this.isSpeakerOn);
      
      // Adjust volume on audio element
      const audio = this.$refs.incomingAudio;
      if (audio) {
        audio.volume = this.isSpeakerOn ? 1.0 : 0.7;
      }
    },

    sendDTMF(digit) {
      console.log("[IncomingCallDialog] 📞 Sending DTMF:", digit);
      this.$store.dispatch("dialer/sendDTMF", digit);
    },
  },
};
</script>

<style scoped>
.incoming-call-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* Ringing State Header */
.ringing-header {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
}

/* Connected State Header */
.connected-header {
  background: linear-gradient(135deg, #2e7d32 0%, #43a047 100%);
}

/* Pulse animation for ringing */
.pulse-ring {
  animation: pulse-ring 1.5s ease-in-out infinite;
}

@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

/* Call Duration Display */
.call-duration-display {
  font-size: 28px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
}

/* Call Controls */
.call-controls {
  gap: 8px;
}

/* DTMF Dialpad */
.dtmf-dialpad {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 8px;
}

.dialpad-btn {
  height: 48px !important;
  min-width: unset !important;
}

.digit-main {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
}

.digit-sub {
  font-size: 9px;
  color: #9e9e9e;
  line-height: 1;
  margin-top: 2px;
}

/* Status animations */
.caption.orange--text,
.caption.blue--text {
  animation: pulse 1.5s ease-in-out infinite alternate;
}

@keyframes pulse {
  from { opacity: 1; }
  to { opacity: 0.6; }
}

/* Responsive adjustments */
@media (max-width: 400px) {
  .call-controls .v-btn.v-btn--fab.v-size--small {
    width: 40px;
    height: 40px;
  }
  
  .call-duration-display {
    font-size: 24px;
  }
}

.v-dialog {
  z-index: 9999 !important;
}
</style>
